import { supabase } from './supabase';

export interface AdminStats {
  totalPartners: number;
  totalCoaches: number;
  totalRespondents: number;
  totalAssessments: number;
  totalCodes: number;
  codesUsed: number;
  codesAvailable: number;
  aiAnalysisCount: number;
}

export interface MonthlyGrowth {
  month: string;
  label: string;
  users: number;
  assessments: number;
}

export interface PartnerOverview {
  partnerId: string;
  partnerName: string;
  email: string;
  dateJoined: string;
  coachCount: number;
  maxCoaches: number;
  respondentCount: number;
  assessmentCount: number;
  codesUsed: number;
  codesTotal: number;
}

export interface CoachTierBreakdown {
  tier: string;
  count: number;
  withAiAccess: number;
}

export interface RecentActivity {
  id: string;
  type: 'assessment' | 'registration';
  userName: string;
  detail: string;
  timestamp: string;
}

export interface InactiveCoachAdmin {
  coachId: string;
  coachName: string;
  partnerName: string;
  lastActivityDate: string | null;
}

export interface AdminCodeRedemption {
  totalCodes: number;
  usedCodes: number;
  rate: number;
}

export interface MonthlyAssessmentAdmin {
  month: string;
  label: string;
  count: number;
}

export async function fetchAdminStats(): Promise<AdminStats> {
  const [partnersRes, coachesRes, respondentsRes, resultsRes, codesRes, aiRes] = await Promise.all([
    supabase.from('partners').select('id', { count: 'exact', head: true }),
    supabase.from('coaches').select('id', { count: 'exact', head: true }),
    supabase.from('respondents').select('id', { count: 'exact', head: true }),
    supabase.from('results').select('id', { count: 'exact', head: true }),
    supabase.from('assessment_codes').select('is_used'),
    supabase.from('results').select('id', { count: 'exact', head: true }).not('ai_analysis', 'is', null),
  ]);

  const codes = codesRes.data || [];
  const used = codes.filter(c => c.is_used).length;
  const available = codes.filter(c => !c.is_used).length;

  return {
    totalPartners: partnersRes.count || 0,
    totalCoaches: coachesRes.count || 0,
    totalRespondents: respondentsRes.count || 0,
    totalAssessments: resultsRes.count || 0,
    totalCodes: codes.length,
    codesUsed: used,
    codesAvailable: available,
    aiAnalysisCount: aiRes.count || 0,
  };
}

export async function fetchAdminCodeRedemption(): Promise<AdminCodeRedemption> {
  const { count: totalCodes } = await supabase
    .from('assessment_codes')
    .select('*', { count: 'exact', head: true });

  const { count: usedCodes } = await supabase
    .from('assessment_codes')
    .select('*', { count: 'exact', head: true })
    .eq('is_used', true);

  const total = totalCodes || 0;
  const used = usedCodes || 0;

  return {
    totalCodes: total,
    usedCodes: used,
    rate: total > 0 ? Math.round((used / total) * 100) : 0,
  };
}

export async function fetchAssessmentsOverTimeAdmin(): Promise<MonthlyAssessmentAdmin[]> {
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 5);
  sixMonthsAgo.setDate(1);
  sixMonthsAgo.setHours(0, 0, 0, 0);

  const { data: results } = await supabase
    .from('results')
    .select('completed_at')
    .gte('completed_at', sixMonthsAgo.toISOString())
    .order('completed_at', { ascending: true });

  const monthMap: Record<string, number> = {};
  const months: { month: string; label: string }[] = [];

  for (let i = 0; i < 6; i++) {
    const d = new Date();
    d.setMonth(d.getMonth() - (5 - i));
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('default', { month: 'short' });
    months.push({ month: key, label });
    monthMap[key] = 0;
  }

  (results || []).forEach(r => {
    if (r.completed_at) {
      const d = new Date(r.completed_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (monthMap[key] !== undefined) {
        monthMap[key]++;
      }
    }
  });

  return months.map(m => ({
    month: m.month,
    label: m.label,
    count: monthMap[m.month],
  }));
}

export async function fetchPartnerOverviews(): Promise<PartnerOverview[]> {
  const { data: partners } = await supabase
    .from('partners')
    .select('id, company_name, contact_email, max_coaches, created_at, users!partners_user_id_fkey(full_name, email)');

  if (!partners || partners.length === 0) return [];

  const partnerIds = partners.map(p => p.id);

  const { data: coaches } = await supabase
    .from('coaches')
    .select('id, partner_id')
    .in('partner_id', partnerIds);

  const { data: codes } = await supabase
    .from('assessment_codes')
    .select('partner_id, is_used')
    .in('partner_id', partnerIds);

  const coachMap: Record<string, string[]> = {};
  (coaches || []).forEach(c => {
    if (c.partner_id) {
      if (!coachMap[c.partner_id]) coachMap[c.partner_id] = [];
      coachMap[c.partner_id].push(c.id);
    }
  });

  const allCoachIds = (coaches || []).map(c => c.id);

  let respondentMap: Record<string, number> = {};
  let assessmentMap: Record<string, number> = {};

  if (allCoachIds.length > 0) {
    const { data: respondents } = await supabase
      .from('respondents')
      .select('coach_id')
      .in('coach_id', allCoachIds);

    (respondents || []).forEach(r => {
      if (r.coach_id) {
        respondentMap[r.coach_id] = (respondentMap[r.coach_id] || 0) + 1;
      }
    });

    const { data: results } = await supabase
      .from('results')
      .select('coach_id')
      .in('coach_id', allCoachIds);

    (results || []).forEach(r => {
      if (r.coach_id) {
        assessmentMap[r.coach_id] = (assessmentMap[r.coach_id] || 0) + 1;
      }
    });
  }

  const codeMap: Record<string, { used: number; total: number }> = {};
  (codes || []).forEach(c => {
    if (c.partner_id) {
      if (!codeMap[c.partner_id]) codeMap[c.partner_id] = { used: 0, total: 0 };
      codeMap[c.partner_id].total++;
      if (c.is_used) codeMap[c.partner_id].used++;
    }
  });

  return partners.map(p => {
    const partnerCoachIds = coachMap[p.id] || [];
    const respondentCount = partnerCoachIds.reduce((sum, cid) => sum + (respondentMap[cid] || 0), 0);
    const assessmentCount = partnerCoachIds.reduce((sum, cid) => sum + (assessmentMap[cid] || 0), 0);
    const codeStats = codeMap[p.id] || { used: 0, total: 0 };

    return {
      partnerId: p.id,
      partnerName: p.company_name || (p.users as any)?.full_name || (p.users as any)?.email || 'Unknown',
      email: p.contact_email || (p.users as any)?.email || '',
      dateJoined: p.created_at || '',
      coachCount: partnerCoachIds.length,
      maxCoaches: p.max_coaches || 10,
      respondentCount,
      assessmentCount,
      codesUsed: codeStats.used,
      codesTotal: codeStats.total,
    };
  }).sort((a, b) => b.assessmentCount - a.assessmentCount);
}

export async function fetchCoachTierBreakdown(): Promise<CoachTierBreakdown[]> {
  const { data: coaches } = await supabase
    .from('coaches')
    .select('tier, ai_analysis_access');

  if (!coaches || coaches.length === 0) return [];

  const tierMap: Record<string, { count: number; withAi: number }> = {};

  coaches.forEach(c => {
    const tier = c.tier || 'basic';
    if (!tierMap[tier]) tierMap[tier] = { count: 0, withAi: 0 };
    tierMap[tier].count++;
    if (c.ai_analysis_access) tierMap[tier].withAi++;
  });

  const tierOrder = ['basic', 'basic_plus', 'advanced', 'partner'];

  return tierOrder
    .filter(t => tierMap[t])
    .map(t => ({
      tier: t,
      count: tierMap[t].count,
      withAiAccess: tierMap[t].withAi,
    }));
}

export async function fetchRecentActivity(): Promise<RecentActivity[]> {
  const { data: results } = await supabase
    .from('results')
    .select('id, completed_at, users!results_user_id_fkey(full_name, email), coaches!results_coach_id_fkey(users!coaches_user_id_fkey(full_name, email))')
    .order('completed_at', { ascending: false })
    .limit(10);

  const assessments: RecentActivity[] = (results || []).map(r => ({
    id: r.id,
    type: 'assessment' as const,
    userName: (r.users as any)?.full_name || (r.users as any)?.email || 'Unknown',
    detail: `Coach: ${(r.coaches as any)?.users?.full_name || (r.coaches as any)?.users?.email || 'Unknown'}`,
    timestamp: r.completed_at || '',
  }));

  const { data: recentUsers } = await supabase
    .from('users')
    .select('id, full_name, email, role, created_at')
    .order('created_at', { ascending: false })
    .limit(5);

  const registrations: RecentActivity[] = (recentUsers || []).map(u => ({
    id: u.id,
    type: 'registration' as const,
    userName: u.full_name || u.email || 'Unknown',
    detail: `Role: ${u.role || 'respondent'}`,
    timestamp: u.created_at || '',
  }));

  return [...assessments, ...registrations]
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 15);
}

export async function fetchInactiveCoachesAdmin(): Promise<InactiveCoachAdmin[]> {
  const { data: coaches } = await supabase
    .from('coaches')
    .select('id, partner_id, users!coaches_user_id_fkey(full_name, email)')
    .eq('is_active', true);

  if (!coaches || coaches.length === 0) return [];

  const partnerIds = [...new Set(coaches.filter(c => c.partner_id).map(c => c.partner_id!))];
  let partnerNameMap: Record<string, string> = {};

  if (partnerIds.length > 0) {
    const { data: partners } = await supabase
      .from('partners')
      .select('id, company_name')
      .in('id', partnerIds);

    (partners || []).forEach(p => {
      partnerNameMap[p.id] = p.company_name || 'Unknown';
    });
  }

  const coachIds = coaches.map(c => c.id);
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const { data: recentResults } = await supabase
    .from('results')
    .select('coach_id, completed_at')
    .in('coach_id', coachIds)
    .order('completed_at', { ascending: false });

  const lastActivityMap: Record<string, string | null> = {};
  coachIds.forEach(id => { lastActivityMap[id] = null; });

  (recentResults || []).forEach(r => {
    if (r.coach_id && !lastActivityMap[r.coach_id]) {
      lastActivityMap[r.coach_id] = r.completed_at;
    }
  });

  return coaches
    .filter(c => {
      const lastDate = lastActivityMap[c.id];
      if (!lastDate) return true;
      return new Date(lastDate) < thirtyDaysAgo;
    })
    .map(c => ({
      coachId: c.id,
      coachName: (c.users as any)?.full_name || (c.users as any)?.email || 'Unknown',
      partnerName: c.partner_id ? partnerNameMap[c.partner_id] || 'No partner' : 'No partner',
      lastActivityDate: lastActivityMap[c.id],
    }));
}

export async function fetchMonthlyGrowth(): Promise<MonthlyGrowth[]> {
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 5);
  sixMonthsAgo.setDate(1);
  sixMonthsAgo.setHours(0, 0, 0, 0);

  const [usersRes, resultsRes] = await Promise.all([
    supabase
      .from('users')
      .select('created_at')
      .gte('created_at', sixMonthsAgo.toISOString()),
    supabase
      .from('results')
      .select('completed_at')
      .gte('completed_at', sixMonthsAgo.toISOString()),
  ]);

  const months: { month: string; label: string }[] = [];
  const userMap: Record<string, number> = {};
  const assessmentMap: Record<string, number> = {};

  for (let i = 0; i < 6; i++) {
    const d = new Date();
    d.setMonth(d.getMonth() - (5 - i));
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('default', { month: 'short' });
    months.push({ month: key, label });
    userMap[key] = 0;
    assessmentMap[key] = 0;
  }

  (usersRes.data || []).forEach(u => {
    if (u.created_at) {
      const d = new Date(u.created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (userMap[key] !== undefined) userMap[key]++;
    }
  });

  (resultsRes.data || []).forEach(r => {
    if (r.completed_at) {
      const d = new Date(r.completed_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (assessmentMap[key] !== undefined) assessmentMap[key]++;
    }
  });

  return months.map(m => ({
    month: m.month,
    label: m.label,
    users: userMap[m.month],
    assessments: assessmentMap[m.month],
  }));
}
